/**
 * EntityPage
 *
 * Renders the dereferenced view of a data, ontology, or arbitrary RDF entity.
 * When `type` is `data` or `ontology` the URI is reconstructed from the route
 * path; when `type` is `dereference` the URI is read from the `?uri=` query
 * parameter, allowing any URI (e.g. external ones such as `rdfs:label`) to be
 * described via DESCRIBE against the local knowledge graph.
 *
 * Two views, switched with `?view=friendly|technical` (default friendly,
 * the last choice is remembered):
 *  - friendly: cards built from the SHACL view shapes (EntityShapeView);
 *    falls back to the technical view when no shape matches the entity
 *  - technical: the generic predicate/value table
 *
 * Sections:
 *  - Top bar: RDF format buttons (Turtle / RDF/XML / JSON-LD) and view switch
 *  - Properties (collapsible)
 *  - Geometry map (collapsible, only if gsp:hasGeometry present)
 *  - See Also (collapsible, only if same-type resources exist)
 */

import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumbs,
  Button,
  ButtonLink,
  Callout,
  Container,
  EmptyState,
  Icon,
  Link,
  LoadingState,
  PageHeader,
  Segmented,
  Text,
} from '@bn-apps/dim-ui-react';
import { ArrowOutward, GridOn, Map, Link as LinkIcon } from '@bn-apps/design-system-icons';
import { useEntity, shortenUri } from '@hooks/useEntity';
import { useGeometry } from '@hooks/useGeometry';
import { useSeeAlso } from '@hooks/useSeeAlso';
import { useEntityView, useOverviewDefault } from '@hooks/useEntityView';
import { useAuth } from '@hooks/useAuth';
import { isInternalUri, pathToUri, uriToDereferencePath } from '@utils/prefix.utils';
import EntityPropertyTable from './EntityPropertyTable';
import EntityMap from './EntityMap';
import EntityRdfViewer from './EntityRdfViewer';
import EntitySection from './EntitySection';
import EntityShapeView from './view/EntityShapeView';
import routes from '@config/routes';
import { atlasLink } from '@utils/atlas.utils';
import styles from './EntityPage.module.scss';
import appStyles from '@/App.module.scss';

interface EntityPageProps {
  /** Whether this is a data, ontology, or generic dereference entity */
  type: 'data' | 'ontology' | 'dereference';
}

const SHOW_URIS_STORAGE_KEY = 'dim-entity-show-uris';
const VIEW_STORAGE_KEY = 'dim-entity-view';
const VIEW_PARAM = 'view';

type EntityViewMode = 'friendly' | 'technical';

function readViewPreference(): EntityViewMode {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'technical' ? 'technical' : 'friendly';
  } catch {
    return 'friendly';
  }
}

function readShowUrisPreference(): boolean {
  try {
    return localStorage.getItem(SHOW_URIS_STORAGE_KEY) === 'true';
  } catch {
    // localStorage may be unavailable in private browsing or restricted contexts.
    return false;
  }
}

function isHttpUri(uri: string): boolean {
  try {
    const parsed = new URL(uri);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

const EntityPage = ({ type }: EntityPageProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { '*': entityPath } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // An explicit ?view= is remembered as the last choice.
  const viewParam = searchParams.get(VIEW_PARAM);
  const explicitView = viewParam === 'technical' || viewParam === 'friendly' ? viewParam : null;

  useEffect(() => {
    if (viewParam !== 'technical' && viewParam !== 'friendly') return;
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, viewParam);
    } catch {
      // Ignore storage failures; the query parameter still selects the view.
    }
  }, [viewParam]);

  const viewHref = (mode: EntityViewMode): string => {
    const params = new URLSearchParams(location.search);
    params.set(VIEW_PARAM, mode);
    return `${location.pathname}?${params.toString()}`;
  };

  // URI view is opt-in. Friendly names are shown by default.
  const [showUris, setShowUris] = useState(readShowUrisPreference);

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_URIS_STORAGE_KEY, String(showUris));
    } catch {
      // Ignore storage failures; the preference still works for this page visit.
    }
  }, [showUris]);

  // Reconstruct full URI: for data/ontology routes from the path,
  // for the generic dereference route from the `?uri=` query parameter.
  const uri =
    type === 'dereference'
      ? decodeURIComponent(searchParams.get('uri') || '')
      : pathToUri(entityPath || '', type);

  // For the dereference route the URI is required; surface a clear error
  // instead of silently spinning on an empty URI.
  const missingUri = type === 'dereference' && !uri;

  const { properties, prefixes, loading, error, label } = useEntity(uri, i18n.language);
  // An explicit ?view= wins; a class whose shapes are not ready for the overview opens in the technical view
  const overviewDefault = useOverviewDefault(properties);
  const viewMode: EntityViewMode = explicitView ?? (overviewDefault === false ? 'technical' : readViewPreference());
  // Anonymous visitors only get the data published to RINF and the ontologies
  // from /describe (see deploy/nginx.conf), so the page says so.
  const { user, loading: authLoading, login } = useAuth();
  const publicOnly = !authLoading && !user?.isAuthenticated && isInternalUri(uri);
  const { wkt, loading: geoLoading } = useGeometry(properties);
  const displayError = missingUri ? t('entity.missingUriError') : error;
  const displayLoading = !missingUri && loading;
  const hasResolvedEntity = !displayLoading && !displayError;
  const hasProperties = properties.length > 0;

  const friendlyEnabled = viewMode === 'friendly' && hasResolvedEntity && hasProperties;
  const entityView = useEntityView(uri, properties, friendlyEnabled);
  const viewIsCurrent = entityView.uri === uri;
  const showFriendly = friendlyEnabled && viewIsCurrent && entityView.status === 'ready';
  // Until the model belongs to this entity (the render before the hook's effect,
  // or right after navigating) show the loader instead of a flash of another view.
  // The overview has no See also section; only a class without view shapes falls back to it
  const { resources: seeAlsoResources, typeUri, loading: seeAlsoLoading } = useSeeAlso(
    uri,
    properties,
    !friendlyEnabled || entityView.status === 'noShape' || entityView.status === 'error'
  );
  const friendlyLoading =
    friendlyEnabled && (!viewIsCurrent || entityView.status === 'loading' || entityView.status === 'idle');

  const shortenedUri = Object.keys(prefixes).length > 0 ? shortenUri(uri, prefixes) : uri;
  const displayLabel = label || shortenedUri;

  const typeLabel =
    type === 'data'
      ? t('entity.dataEntity')
      : type === 'ontology'
        ? t('entity.ontologyEntity')
        : t('entity.dereferenceEntity');

  // The friendly view shows the geometry in its hero panel.
  const showGeometry = !geoLoading && wkt !== null && !showFriendly && !friendlyLoading;
  const showSeeAlso = !seeAlsoLoading && seeAlsoResources.length > 0;
  const showOpenOriginalUri =
    type === 'dereference' &&
    !missingUri &&
    hasResolvedEntity &&
    !hasProperties &&
    !isInternalUri(uri) &&
    isHttpUri(uri);
  const emptyStateTitle = publicOnly ? t('entity.publicOnly.notPublished') : t('entity.noProperties');
  const emptyStateDescription = showOpenOriginalUri
    ? t('entity.noPropertiesExternalDescription')
    : publicOnly
      ? t('entity.publicOnly.notPublishedDescription')
      : t('entity.noPropertiesDescription');

  const viewSwitch = hasProperties && !(friendlyEnabled && viewIsCurrent && entityView.status === 'noShape') && (
    <Segmented
      label={t('entity.view.switchLabel')}
      hideLabel
      size="sm"
      value={viewMode}
      onChange={(mode) => navigate(viewHref(mode), { replace: true })}
      options={(['friendly', 'technical'] as const).map((mode) => ({ value: mode, label: t(`entity.view.${mode}`) }))}
    />
  );

  return (
    <Container className={appStyles.pageContent}>
      <div className={styles.entityPage}>
        {/* The overview draws its own header (breadcrumb, name, status) */}
        {!showFriendly && (
          <PageHeader
            title={displayLabel}
            breadcrumbs={
              <Breadcrumbs items={[{ label: t('common.home'), href: routes.home }, { label: typeLabel }, { label: displayLabel }]} />
            }
            lead={
              <Link href={uri} isExternal externalLabel={uri}>
                {uri}
              </Link>
            }
          />
        )}

        {publicOnly && hasResolvedEntity && hasProperties && (
          <Callout
            status="info"
            className={styles.publicNotice}
            actions={
              <Button variant="secondary" size="sm" onClick={login}>
                {t('entity.publicOnly.signIn')}
              </Button>
            }
          >
            {t('entity.publicOnly.notice')}
          </Callout>
        )}

        {/* The overview has the triples lens instead, and puts the switch beside its breadcrumbs */}
        {hasResolvedEntity && !showFriendly && (
          <div className={styles.entityTopBar}>
            <div className={styles.entityTopBarMain}>
              <EntityRdfViewer uri={uri} properties={properties} prefixes={prefixes} />
            </div>
            {/* DIM Atlas is for signed-in users, as the data it maps */}
            {user?.isAuthenticated && isInternalUri(uri) && (
              <ButtonLink variant="secondary" size="sm" href={atlasLink(uri)} iconStart={Map}>
                {t('entity.showOnMap')}
              </ButtonLink>
            )}
            {viewSwitch}
          </div>
        )}

        {(displayLoading || friendlyLoading) && <LoadingState />}

        {displayError && (
          <Callout status="critical" title={t('entity.error')}>
            {displayError}
          </Callout>
        )}

        {friendlyEnabled && viewIsCurrent && entityView.status === 'error' && (
          <Text tone="secondary" className={styles.viewNotice}>
            {t('entity.view.shapesError')}
          </Text>
        )}

        {hasResolvedEntity && !friendlyLoading && (
          <div className={styles.sectionsWrapper}>
            {showFriendly ? (
              <EntityShapeView
                model={entityView}
                entityProperties={properties}
                prefixes={prefixes}
                label={displayLabel}
                wkt={geoLoading ? null : wkt}
                viewSwitch={viewSwitch || null}
              />
            ) : (
              <EntitySection
                title={t('entity.sections.properties')}
                icon={<Icon component={GridOn} size={24} />}
                defaultOpen
              >
                {hasProperties ? (
                  <EntityPropertyTable
                    properties={properties}
                    prefixes={prefixes}
                    showUris={showUris}
                    onShowUrisChange={setShowUris}
                  />
                ) : (
                  <EmptyState
                    title={emptyStateTitle}
                    actions={
                      publicOnly ? (
                        <Button variant="secondary" size="sm" onClick={login}>
                          {t('entity.publicOnly.signIn')}
                        </Button>
                      ) : showOpenOriginalUri ? (
                        <ButtonLink variant="secondary" size="sm" href={uri} isExternal iconEnd={ArrowOutward}>
                          {t('entity.openOriginalUri')}
                        </ButtonLink>
                      ) : undefined
                    }
                  >
                    {emptyStateDescription}
                  </EmptyState>
                )}
              </EntitySection>
            )}

            {/* Geometry map */}
            {showGeometry && (
              <EntitySection
                title={t('entity.sections.geometry')}
                icon={<Icon component={Map} size={24} />}
                defaultOpen
              >
                <EntityMap wkt={wkt!} />
              </EntitySection>
            )}

            {/* See Also: the overview's "Connected to" covers it */}
            {showSeeAlso && !showFriendly && (
              <EntitySection
                title={t('entity.sections.seeAlso')}
                icon={<Icon component={LinkIcon} size={24} />}
                defaultOpen={false}
              >
                <ul className={styles.seeAlsoList}>
                  {seeAlsoResources.map((resourceUri) => {
                    const shortened = shortenUri(resourceUri, prefixes);
                    const dereferencePath = uriToDereferencePath(resourceUri);
                    return (
                      <li key={resourceUri} className={styles.seeAlsoItem}>
                        <RouterLink
                          to={dereferencePath}
                          className={styles.internalLink}
                          title={resourceUri}
                        >
                          {shortened}
                        </RouterLink>
                        {typeUri && (
                          <span className={styles.seeAlsoType}>
                            {shortenUri(typeUri, prefixes)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </EntitySection>
            )}
          </div>
        )}

        <div className={styles.helpLink}>
          <Link href={routes.helpDereferencing}>{t('entity.helpLink')}</Link>
        </div>
      </div>
    </Container>
  );
};

export default EntityPage;
